import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function ProductCategoriesViewModel(logout) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [primaryCategories, setProductCategories] = useState({
    data: [],
  });
  const [primaryCategoriesForDropdown, setPrimaryCategoriesForDropdown] = useState([]);

  const defaultFilters = {
    primaryCategory: { value: -1, label: "All Primary Categories" },
  };
  const [filters, setFilters] = useState(defaultFilters);

  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getProductCategories = async function (search, page, filter) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(`products/categories`, payload);
    setLoading(false);
    if (callResult.success) {
      setProductCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product categories.");
    }
  };

  const getPrimaryCategoriesForDropdown = async function () {
    let callResult = await makePostApiCall(`products/categories/dropdown`);
    if (callResult.success) {
      setPrimaryCategoriesForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message || "Could not fetch categories");
    }
  };

  const resetFilters = () => {
    const filterReset = {
      primaryCategory: { value: -1, label: "All Primary Categories" },
    };
    setFilters(filterReset);
    getProductCategories(null, 1, filterReset);
  };

  const deleteCategory = async function (id) {
    const callResult = await makePostApiCall(`products/categories/delete/${id}`, {});
    if (callResult.success) {
      showSuccess(callResult.data?.message || "Category deleted");
      getProductCategories(null, 1, filters);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Could not delete category",
          callResult.data?.message || "Please try again."
        );
      }
    }
  };

  const askDeleteCategory = function (category) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete category"
          buttonMessage="Delete"
          message={`Are you sure you want to delete "${category.name}"? This action cannot be undone.`}
          setOpen={setEmptyModelOpen}
          confirmAction={async () => {
            await deleteCategory(category.id);
            setEmptyModelOpen({ childComponent: null, open: false });
          }}
        />
      ),
      open: true,
    });
  };

  const primaryCategoryOptions = [
    { value: -1, label: "All Primary Categories" },
    ...primaryCategoriesForDropdown,
  ];

  const filterComponents = [
    <FilterSingleSelectDropdown
      key="primaryCategory"
      header="Primary Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          primaryCategory: value,
        })
      }
      options={primaryCategoryOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="primaryCategory"
      value={filters.primaryCategory}
    />,
  ];

  return {
    emptyModal,
    setEmptyModelOpen,
    getProductCategories,
    loading,
    primaryCategories,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    askDeleteCategory,
  };
}
